import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SocialProof from "@/components/SocialProof";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Users, Scale, FileText, Banknote, Home, Key } from "lucide-react";

const SERVICES = [
  { key: "Realtor", icon: Users, label: "Realtor", body: "A trusted local agent who knows your market." },
  { key: "Lawyer", icon: Scale, label: "Real estate lawyer", body: "Contracts and closing handled right." },
  { key: "Title", icon: FileText, label: "Title company", body: "Clean title search and secure closing." },
  { key: "Mortgage", icon: Banknote, label: "Mortgage loan", body: "Competitive financing options." },
];

const COPY = {
  buy: {
    icon: Key,
    eyebrow: "Buying a home",
    h1: "Buy your next home with a team that has your back",
    sub: "Saving America Money connects you with a trusted realtor, real estate lawyer, title company, and mortgage lender — all in one place, so your purchase goes smoothly.",
    cta: "Get matched to buy",
  },
  sell: {
    icon: Home,
    eyebrow: "Selling your home",
    h1: "Sell your home for more, with less hassle",
    sub: "Saving America Money lines up the right realtor, real estate lawyer, title company, and financing partners to help you sell quickly and for top dollar.",
    cta: "Get matched to sell",
  },
};

const EMPTY = { name: "", email: "", phone: "", city: "", state_abbr: "", zip: "", timeframe: "", notes: "" };

export default function RealEstateLanding({ intent = "buy" }) {
  const c = COPY[intent] || COPY.buy;
  const [form, setForm] = useState(EMPTY);
  const [needs, setNeeds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleNeed = (k) => setNeeds((n) => (n.includes(k) ? n.filter((x) => x !== k) : [...n, k]));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.email.trim()) return toast.error("Please enter your email");
    setBusy(true);
    try {
      await api.post("/realestate-lead", { ...form, intent, needs });
      setDone(true);
      toast.success("Thanks! We'll be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12" data-testid={`realestate-${intent}-page`}>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-crimson">
            <c.icon className="h-4 w-4" />{c.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-sam-navy mt-3">{c.h1}</h1>
          <p className="text-sam-slate mt-4 text-base">{c.sub}</p>

          <div className="grid sm:grid-cols-2 gap-3 mt-8">
            {SERVICES.map((s) => (
              <div key={s.key} className="rounded-xl border border-border bg-card p-4" data-testid={`service-${s.key.toLowerCase()}`}>
                <div className="flex items-center gap-2 font-display font-bold text-sam-navy"><s.icon className="h-4 w-4 text-sam-crimson" />{s.label}</div>
                <p className="text-xs text-sam-slate mt-1">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          {done ? (
            <div className="text-center py-8" data-testid="realestate-success">
              <CheckCircle2 className="h-12 w-12 text-sam-green mx-auto mb-3" />
              <h2 className="font-display text-xl font-bold text-sam-navy">You're all set!</h2>
              <p className="text-sam-slate text-sm mt-1">A Saving America Money specialist will reach out to line up your {intent === "sell" ? "home sale" : "home purchase"} team.</p>
              <Link to="/" className="inline-block mt-5"><Button variant="outline" data-testid="realestate-home-btn">Back to home</Button></Link>
            </div>
          ) : (
            <>
              <h2 className="font-display text-xl font-bold text-sam-navy">{c.cta}</h2>
              <p className="text-sm text-sam-slate mb-4">Tell us a little about your plans — no obligation.</p>
              <div className="space-y-3">
                <div><Label className="text-xs">Full name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="re-name" placeholder="Jane Smith" /></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="re-email" placeholder="you@email.com" /></div>
                  <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="re-phone" placeholder="(555) 555-5555" /></div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="re-city" placeholder="Monett" /></div>
                  <div><Label className="text-xs">State</Label><Input value={form.state_abbr} onChange={(e) => set("state_abbr", e.target.value.toUpperCase())} data-testid="re-state" placeholder="MO" maxLength={2} /></div>
                  <div><Label className="text-xs">ZIP</Label><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} data-testid="re-zip" placeholder="65708" /></div>
                </div>
                <div>
                  <Label className="text-xs">Timeframe</Label>
                  <Select value={form.timeframe} onValueChange={(v) => set("timeframe", v)}>
                    <SelectTrigger data-testid="re-timeframe"><SelectValue placeholder="When are you looking to move?" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ASAP">As soon as possible</SelectItem>
                      <SelectItem value="1-3 months">1–3 months</SelectItem>
                      <SelectItem value="3-6 months">3–6 months</SelectItem>
                      <SelectItem value="6-12 months">6–12 months</SelectItem>
                      <SelectItem value="Just researching">Just researching</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">What help do you need?</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {SERVICES.map((s) => (
                      <button type="button" key={s.key} onClick={() => toggleNeed(s.key)} data-testid={`need-${s.key.toLowerCase()}`}
                        className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${needs.includes(s.key) ? "bg-sam-crimson text-white border-sam-crimson" : "bg-white text-sam-navy border-border hover:border-sam-crimson/40"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-xs">Anything else? (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="re-notes" placeholder="Budget, neighborhoods, must-haves…" /></div>
                <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={submit} disabled={busy} data-testid="re-submit-btn">
                  {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : c.cta}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <SocialProof />
    </div>
  );
}
